import BulkImporter from './components/seller/BulkImporter';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
// NEW: Added HelmetProvider for SEO
import { HelmetProvider } from 'react-helmet-async';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot, writeBatch, doc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { getToken } from 'firebase/messaging'; 
import { auth, db, messaging } from './firebase/config'; 

// Page Imports
import AddItem from './components/seller/AddItem';
import Feed from './components/buyer/Feed';
import Auth from './components/auth/Auth';
import ItemDetail from './components/buyer/ItemDetail';
import Profile from './components/auth/Profile';
import AdminDashboard from './components/admin/AdminDashboard';
import Inbox from './components/buyer/Inbox'; 
import SellerProfile from './components/buyer/SellerProfile';
import BountyBoard from './components/buyer/BountyBoard'; 
import SellWithUs from './components/buyer/SellWithUs'; 
// 🚨 NEW: Import the LeaveReview component
import LeaveReview from './components/buyer/LeaveReview'; 

// Compliance & Widget Imports
import Footer from './components/Footer';
import Legal from './components/Legal';
import SupportWidget from './components/buyer/SupportWidget';
import InstallBanner from './components/InstallBanner'; 

function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('user'); 
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // --- RESPONSIVE STATES ---
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- REQUEST PUSH NOTIFICATION PERMISSION ---
  const requestPushPermissions = async (currentUser) => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const currentToken = await getToken(messaging, { vapidKey: 'BFwXpNto5u_pgmgGVREazgVwbtOVhKRsWNoTs7xqbWooU6rz8pxEk7hHoD4i5lQKl-vz1ZLCiLmlosFHfMZ7Txo' });
        if (currentToken) {
          await setDoc(doc(db, 'users', currentUser.uid), { fcmToken: currentToken }, { merge: true });
          console.log("Push notifications enabled!");
        }
      }
    } catch (error) {
      console.error('An error occurred while requesting push permissions:', error);
    }
  };

  // 1. Listen for Auth State & Role
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        requestPushPermissions(currentUser);

        const userDocRef = doc(db, 'users', currentUser.uid);
        onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            setUserRole(docSnap.data().role || 'user');
          }
        });
      } else {
        setUserRole('user');
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // 2. Listen for Unread DB Notifications
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }
    const q = query(
      collection(db, `users/${user.uid}/notifications`), 
      where("read", "==", false)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setNotifications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [user]);

  // 3. Mark all as read when opening the dropdown
  const handleOpenNotifications = async () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown && notifications.length > 0) {
      const batch = writeBatch(db);
      notifications.forEach(notif => {
        const notifRef = doc(db, `users/${user.uid}/notifications`, notif.id);
        batch.update(notifRef, { read: true });
      });
      await batch.commit();
    }
  };

  // 4. Track Unique Analytics Views
  useEffect(() => {
    const hasVisited = sessionStorage.getItem("hasVisited");

    if (!hasVisited) {
      const countVisitor = async () => {
        try {
          const counterRef = doc(db, "analytics", "traffic");
          await updateDoc(counterRef, {
            viewCount: increment(1)
          });
          sessionStorage.setItem("hasVisited", "true");
        } catch (error) {
          console.error("Failed to log view:", error);
        }
      };

      countVisitor();
    }
  }, []);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <HelmetProvider>
      <Router>
        <div style={{ fontFamily: 'sans-serif', backgroundColor: '#f9f9f9', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          
          {/* --- RESPONSIVE HEADER --- */}
          <header style={{ backgroundColor: '#1a1a1a', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
            
            <Link to="/" onClick={closeMenu} style={{ color: 'white', textDecoration: 'none' }}>
              <h1 style={{ margin: 0, fontSize: '24px', letterSpacing: '1px' }}>NUNDINARIA</h1>
            </Link>

            {/* Hamburger Icon & Mobile Bell */}
            {isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {user && (
                  <div style={{ position: 'relative' }}>
                    <button onClick={handleOpenNotifications} style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer', padding: 0 }}>
                      🔔
                      {notifications.length > 0 && (
                        <span style={{ position: 'absolute', top: '-5px', right: '-10px', backgroundColor: 'red', color: 'white', borderRadius: '50%', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}>
                          {notifications.length}
                        </span>
                      )}
                    </button>
                  </div>
                )}
                <button 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
                  style={{ background: 'none', border: 'none', color: 'white', fontSize: '28px', cursor: 'pointer', padding: 0, lineHeight: '1' }}
                >
                  {mobileMenuOpen ? '✕' : '☰'}
                </button>
              </div>
            )}

            {/* Navigation Links */}
            <nav style={{ 
              display: isMobile ? (mobileMenuOpen ? 'flex' : 'none') : 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: '20px', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              position: isMobile ? 'absolute' : 'static',
              top: isMobile ? '60px' : 'auto',
              left: isMobile ? '0' : 'auto',
              right: isMobile ? '0' : 'auto',
              backgroundColor: isMobile ? '#1a1a1a' : 'transparent',
              padding: isMobile ? '20px' : '0',
              zIndex: 900,
              boxShadow: isMobile ? '0 10px 20px rgba(0,0,0,0.5)' : 'none',
              borderTop: isMobile ? '1px solid #333' : 'none'
            }}>
              <Link to="/" onClick={closeMenu} style={{ color: 'white', textDecoration: 'none', width: isMobile ? '100%' : 'auto', padding: isMobile ? '10px 0' : '0', borderBottom: isMobile ? '1px solid #333' : 'none' }}>Marketplace</Link>
              
              <Link to="/bounties" onClick={closeMenu} style={{ color: '#ffc107', textDecoration: 'none', fontWeight: 'bold', width: isMobile ? '100%' : 'auto', padding: isMobile ? '10px 0' : '0', borderBottom: isMobile ? '1px solid #333' : 'none' }}>🎯 ISO Board</Link>
              
              <Link to="/sell-with-us" onClick={closeMenu} style={{ color: '#28a745', textDecoration: 'none', fontWeight: 'bold', width: isMobile ? '100%' : 'auto', padding: isMobile ? '10px 0' : '0', borderBottom: isMobile ? '1px solid #333' : 'none' }}>Why Sell Here?</Link>

              {user ? (
                <>
                  {userRole === 'admin' && (
                    <Link to="/admin" onClick={closeMenu} style={{ color: '#ffc107', textDecoration: 'none', fontWeight: 'bold', width: isMobile ? '100%' : 'auto', padding: isMobile ? '10px 0' : '0', borderBottom: isMobile ? '1px solid #333' : 'none' }}>ADMIN</Link>
                  )}
                  
                  {/* Desktop Notification Bell */}
                  {!isMobile && (
                    <div style={{ position: 'relative' }}>
                      <button onClick={handleOpenNotifications} style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', position: 'relative' }}>
                        🔔
                        {notifications.length > 0 && (
                          <span style={{ position: 'absolute', top: '-5px', right: '-10px', backgroundColor: 'red', color: 'white', borderRadius: '50%', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}>
                            {notifications.length}
                          </span>
                        )}
                      </button>
                    </div>
                  )}

                  <Link to="/inbox" onClick={closeMenu} style={{ color: 'white', textDecoration: 'none', width: isMobile ? '100%' : 'auto', padding: isMobile ? '10px 0' : '0', borderBottom: isMobile ? '1px solid #333' : 'none' }}>💬 Inbox</Link>
                  <Link to="/profile" onClick={closeMenu} style={{ color: '#00d1b2', textDecoration: 'none', fontWeight: 'bold', width: isMobile ? '100%' : 'auto', padding: isMobile ? '10px 0' : '0', borderBottom: isMobile ? '1px solid #333' : 'none' }}>My Account</Link>
                  <Link to="/sell" onClick={closeMenu} style={{ color: '#007bff', backgroundColor: isMobile ? 'transparent' : 'white', padding: isMobile ? '10px 0' : '8px 15px', borderRadius: isMobile ? '0' : '4px', fontWeight: 'bold', width: isMobile ? '100%' : 'auto', borderBottom: isMobile ? '1px solid #333' : 'none' }}>Sell Gear</Link>
                  <button onClick={() => { closeMenu(); signOut(auth); }} style={{ background: 'none', border: isMobile ? 'none' : '1px solid white', color: isMobile ? '#dc3545' : 'white', cursor: 'pointer', padding: isMobile ? '10px 0' : '5px 10px', borderRadius: isMobile ? '0' : '4px', width: isMobile ? '100%' : 'auto', textAlign: 'left', fontWeight: isMobile ? 'bold' : 'normal' }}>Logout</button>
                </>
              ) : (
                <Link to="/login" onClick={closeMenu} style={{ color: 'white', textDecoration: 'none', width: isMobile ? '100%' : 'auto', padding: isMobile ? '10px 0' : '0' }}>Login</Link>
              )}
            </nav>

            {/* Notification Dropdown Panel */}
            {showDropdown && (
              <div style={{ position: 'absolute', top: '65px', right: isMobile ? '10px' : '20px', width: isMobile ? 'calc(100% - 20px)' : '300px', maxWidth: '350px', backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', borderRadius: '8px', overflow: 'hidden', zIndex: 1000, border: '1px solid #eee' }}>
                <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #eee', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between' }}>
                  Notifications
                  <button onClick={() => setShowDropdown(false)} style={{ background: 'none', border: 'none', fontWeight: 'bold', cursor: 'pointer', color: '#888' }}>✕</button>
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {notifications.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>You're all caught up!</div>
                  ) : (
                    notifications.map(notif => (
                      <Link to="/inbox" key={notif.id} onClick={() => { setShowDropdown(false); closeMenu(); }} style={{ display: 'block', padding: '15px', borderBottom: '1px solid #eee', textDecoration: 'none', color: 'black' }}>
                        <strong style={{ display: 'block', fontSize: '14px', marginBottom: '5px' }}>{notif.title}</strong>
                        <span style={{ fontSize: '12px', color: '#666' }}>{notif.body}</span>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            )}
          </header>

          <main style={{ padding: isMobile ? '10px' : '20px', flex: 1 }}>
            <Routes>
              <Route path="/" element={<Feed />} />
              <Route path="/bounties" element={<BountyBoard />} /> 
              <Route path="/sell-with-us" element={<SellWithUs />} />
              <Route path="/bulk-import" element={user ? <BulkImporter /> : <Auth />} /> 
              <Route path="/login" element={<Auth />} />
              <Route path="/item/:id" element={<ItemDetail />} />
              <Route path="/seller/:id" element={<SellerProfile />} />
              <Route path="/sell" element={user ? <AddItem /> : <Auth />} />
              <Route path="/profile" element={user ? <Profile /> : <Auth />} />
              <Route path="/inbox" element={user ? <Inbox /> : <Auth />} />
              <Route path="/admin" element={user && userRole === 'admin' ? <AdminDashboard /> : <Feed />} />
              
              {/* 🚨 NEW: Added secure route for LeaveReview */}
              <Route path="/review/:itemId" element={user ? <LeaveReview /> : <Auth />} />
              
              <Route path="/terms" element={<Legal title="Terms of Service" />} />
              <Route path="/privacy" element={<Legal title="Privacy Policy" />} />
              <Route path="/refunds" element={<Legal title="Refund Policy" />} />

              {/* 404 Catch-All Route Handling Broken Links */}
              <Route path="*" element={
                <div style={{ padding: '100px 20px', textAlign: 'center', color: '#1a1a1a' }}>
                  <h2 style={{ fontSize: '28px', marginBottom: '10px' }}>404 - Page Not Found</h2>
                  <p style={{ color: '#666', marginBottom: '20px' }}>We couldn't find the gear or page you're looking for.</p>
                  <Link to="/" style={{ color: '#007bff', fontWeight: 'bold', textDecoration: 'none' }}>Return to Marketplace</Link>
                </div>
              } />
            </Routes>
          </main>

          <InstallBanner />
          <SupportWidget />
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;